import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ListBuilderService } from '../../../services/list-builder/list-builder.service';
import { LocationService } from '../../../services/location/location.service';
import { TagPrintService } from '../../../services/tag-print/tag-print.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { IoService } from '../../../services/io/io.service';
import { AppComponent } from '../../../app.component';

@Component({
  selector: 'app-list-builder',
  templateUrl: './list-builder.component.html',
  styleUrls: ['./list-builder.component.scss']
})
export class ListBuilderComponent implements OnInit, OnDestroy {

  lists: any[] = [];
  activeList: any = null;
  members: any[] = [];
  activeFields: any[] = [];
  editingName = false;
  editName = '';
  tsl = 0;
  activeDocumentId: string = null;

  @ViewChild('listItemsEl') listItemsEl: ElementRef;

  private listUpdatedSub: Subscription;
  private listsLoadedSub: Subscription;
  private activeFieldsSub: Subscription;
  private routerSub: Subscription;

  constructor(
    private listBuilderService: ListBuilderService,
    private locationService: LocationService,
    private tagPrintService: TagPrintService,
    private authorizationService: AuthorizationService,
    private ioService: IoService,
    private router: Router,
  ) { }

  // ─── DB-configurable list actions (report.listActions) ───────────────────────
  // The footer buttons are defined on the Report document as a `listActions` array
  // (surfaced to the list builder on report load). Each action is generic:
  //   { key, name, class, permission, permissionScope, requiresViewKey,
  //     requiresLocation, confirm,
  //     action: 'navigate' | 'post' | 'print',
  //     route: [...segments],            preserveQueryParams,          // navigate
  //     endpoint, body: {...}, onSuccessNavigate: [...segments],       // post
  //     endpoint, body: {...} }          // print (opens the rendered template)
  // Tokens ($listKey, $listName, $viewKey, $reportKey, $activeLocation,
  // $activeLocationArray, and $response.<field> after a POST) are resolved below.

  private actionContext(): any {
    const loc = this.locationService.getActiveLocation();
    const locId = loc && loc._id ? loc._id : null;
    return {
      viewKey: this.listBuilderService.getActiveViewKey(),
      reportKey: this.listBuilderService.getActiveReportKey(),
      listKey: this.activeList ? this.activeList.key : null,
      listName: this.activeList ? this.activeList.name : null,
      activeLocation: locId,
      activeLocationArray: locId ? [locId] : null,
    };
  }

  // A whole-string token ("$listKey") returns the raw (typed) value; "$response.x"
  // reads a field off the POST response; embedded tokens ("view.$viewKey.update")
  // are string-interpolated.
  private resolveValue(value: any, ctx: any, response?: any): any {
    if (typeof value !== 'string') return value;
    if (/^\$[\w.]+$/.test(value)) {
      if (value.indexOf('$response.') === 0) {
        return response != null ? this.getValueByPath(response, value.slice('$response.'.length)) : null;
      }
      const k = value.slice(1);
      return Object.prototype.hasOwnProperty.call(ctx, k) ? ctx[k] : value;
    }
    return value.replace(/\$(\w+)/g, (_m, k) => (ctx[k] != null ? String(ctx[k]) : ''));
  }

  private resolveDeep(value: any, ctx: any, response?: any): any {
    if (Array.isArray(value)) return value.map(v => this.resolveDeep(v, ctx, response));
    if (value && typeof value === 'object') {
      const out: any = {};
      for (const k of Object.keys(value)) out[k] = this.resolveDeep(value[k], ctx, response);
      return out;
    }
    return this.resolveValue(value, ctx, response);
  }

  isActionVisible(action: any): boolean {
    if (!action || !this.activeList) return false;
    const ctx = this.actionContext();
    if (action.requiresViewKey && !ctx.viewKey) return false;
    if (action.requiresLocation && !ctx.activeLocation) return false;
    if (action.permission) {
      const key = this.resolveValue(action.permission, ctx);
      const scope = action.permissionScope === 'activeLocation' ? ctx.activeLocation : 'any';
      if (!scope) return false;
      if (!this.authorizationService.checkPermission(key, scope, false)) return false;
    }
    return true;
  }

  visibleListActions(): any[] {
    return this.listBuilderService.getActiveListActions().filter(a => this.isActionVisible(a));
  }

  async runListAction(action: any) {
    if (!this.isActionVisible(action)) return;

    if (action.confirm) {
      const dialogRef = AppComponent.showMessage('Confirm', action.confirm, ['Confirm', 'Cancel'], false);
      const result: any = await new Promise(resolve => dialogRef.afterClosed().subscribe(r => resolve(r)));
      if (!result || result.value !== 'Confirm') return;
    }

    const ctx = this.actionContext();

    if (action.action === 'navigate') {
      const route = (action.route || []).map(seg => this.resolveValue(seg, ctx));
      if (route.some(s => s == null || s === '')) return;
      this.router.navigate(route, action.preserveQueryParams ? { queryParamsHandling: 'preserve' } : {});
      return;
    }

    if (action.action === 'print') {
      const body = this.resolveDeep(action.body || {}, ctx);
      try {
        await this.tagPrintService.print(action.endpoint, body, { description: action.name });
      } catch (e) {
        const msg = (e as any)?.msg || (e as any)?.error?.msg || 'Print failed — the tag report or template is unavailable.';
        AppComponent.showSnack(msg, 'OK', 4000);
      }
      return;
    }

    if (action.action === 'post') {
      const body = this.resolveDeep(action.body || {}, ctx);
      try {
        const response: any = await this.ioService.post(action.endpoint, body);
        if (action.onSuccessNavigate) {
          const route = action.onSuccessNavigate.map(seg => this.resolveValue(seg, ctx, response));
          if (route.every(s => s != null && s !== '')) {
            this.router.navigate(route);
          }
        }
      } catch (e) {
        const msg = (e as any)?.msg || (e as any)?.error?.msg || 'Action failed.';
        AppComponent.showSnack(msg, 'OK', 4000);
      }
    }
  }

  ngOnInit(): void {
    this.activeFields = this.listBuilderService.getActiveFields();
    this.lists = this.listBuilderService.getLists();
    this.activeList = this.listBuilderService.getActiveList();
    this.members = this.listBuilderService.getMembers();

    this.listUpdatedSub = this.listBuilderService.listUpdatedObservable().subscribe(data => {
      this.activeList = data.list;
      this.members = data.members;
    });

    this.listsLoadedSub = this.listBuilderService.listsLoadedObservable().subscribe(lists => {
      this.lists = lists;
    });

    this.activeFieldsSub = this.listBuilderService.activeFieldsChangedObservable().subscribe(fields => {
      this.activeFields = fields;
    });

    // Track active document id from URL so list rows mirror the report's "active" highlight
    this.parseActiveDocumentId(this.router.url);
    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
    ).subscribe((e: NavigationEnd) => {
      this.parseActiveDocumentId(e.urlAfterRedirects || e.url);
    });
  }

  ngOnDestroy(): void {
    if (this.listUpdatedSub) this.listUpdatedSub.unsubscribe();
    if (this.listsLoadedSub) this.listsLoadedSub.unsubscribe();
    if (this.activeFieldsSub) this.activeFieldsSub.unsubscribe();
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  // Routes we recognize:
  //   /reports/:key/details/:viewKey/:documentId   → documentId = parts[4]
  //   /reports/:key/:itemType/:documentId          → documentId = parts[3]
  //   /reports/:key/bulk/:viewKey/:listKey         → no document highlight
  //   /reports/:key                                → no document highlight
  private parseActiveDocumentId(url: string) {
    const parts = (url || '').split('?')[0].split('/').filter(p => p);
    if (parts[0] !== 'reports') { this.activeDocumentId = null; return; }
    if (parts[2] === 'bulk') { this.activeDocumentId = null; return; }
    if (parts[2] === 'details' && parts.length >= 5) {
      this.activeDocumentId = parts[4];
      return;
    }
    if (parts.length === 4) {
      this.activeDocumentId = parts[3];
      return;
    }
    this.activeDocumentId = null;
  }

  openDetail(item: any) {
    if (!item || !item._id) return;
    const viewKey = this.listBuilderService.getActiveViewKey();
    const viewRoute = this.listBuilderService.getActiveViewRoute();
    const itemType = this.listBuilderService.getActiveItemType();
    const reportKey = this.listBuilderService.getActiveReportKey();
    const recordId = item._id;

    if (viewKey) {
      if (!this.authorizationService.checkPermission('view.' + viewKey + '.view', 'any', false)) {
        return;
      }
      this.router.navigate(
        ['reports', reportKey, 'details', viewKey, recordId],
        { queryParamsHandling: 'preserve' },
      );
    } else if (viewRoute) {
      this.router.navigate([viewRoute, recordId], { queryParamsHandling: 'preserve' });
    } else if (itemType && reportKey) {
      this.router.navigate(
        ['reports', reportKey, itemType, recordId],
        { queryParamsHandling: 'preserve' },
      );
    }
  }

  onRemoveClick(event: Event, itemId: string) {
    event.stopPropagation();
    this.removeItem(itemId);
  }

  close() {
    this.listBuilderService.closeListBuilder();
  }

  createNewList() {
    const locationId = this.locationService.getActiveLocation()?._id || null;
    const reportKey = this.listBuilderService.getActiveReportKey();
    this.listBuilderService.createList('New List', reportKey, locationId);
  }

  selectList(listKey: string) {
    if (listKey) {
      this.listBuilderService.loadList(listKey);
    }
  }

  removeItem(itemId: string) {
    this.listBuilderService.removeItem(itemId);
  }

  startEditName() {
    if (!this.activeList) return;
    this.editingName = true;
    this.editName = this.activeList.name;
  }

  saveName() {
    if (this.editName && this.editName !== this.activeList?.name) {
      this.listBuilderService.renameList(this.editName);
    }
    this.editingName = false;
  }

  cancelEditName() {
    this.editingName = false;
  }

  deleteList() {
    if (!this.activeList) return;
    const name = this.activeList.name;
    const dialogRef = AppComponent.showMessage(
      'Delete List',
      `Delete the list "${name}"? This cannot be undone.`,
      ['Confirm', 'Cancel'],
      false,
    );
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result.value !== 'Confirm') return;
      this.listBuilderService.deleteList();
    });
  }

  onListScroll(event) {
    this.tsl = event.target.scrollLeft;
  }

  getValueByPath(o, path) {
    if (o == null) return;
    path = path.replace(/\[(\w+)\]/g, '.$1');
    path = path.replace(/^\./, '');
    const a = path.split('.');
    for (let i = 0, n = a.length; i < n; ++i) {
      const k = a[i];
      if (k in o) {
        o = o[k];
      } else {
        return;
      }
    }
    return o;
  }
}
